<script lang="ts" setup>
import { defineAsyncComponent, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { Form as VeeForm } from 'vee-validate'

/* Stores */
import { useBasketStore } from '@/store/basket/basket'
import { useUserStore } from '@/store/user/user'
import { useAppStore } from '@/store/app/app'

/* Types */
import { UnprocessableEntityError, ServerError } from '@/api/results'
import { SnackbarType } from '@/store/app/app.types'
import { Item } from '@/api/modules/basket/types'
import { PaymentMethodType, SavedCard } from '@/types/api/api-v3/paymentMethods'
import { CheckoutButtonStateType } from '@/types/basket'

/* Components */
import BasketAddresses from '@/components/Basket/basket-addresses/BasketAddresses.vue'
import BasketTime from '@/components/Basket/basket-time/BasketTime.vue'
import BasketNote from '@/components/Basket/basket-note/BasketNote.vue'
import BasketAdditive from '@/components/Basket/basket-additive/BasketAdditive.vue'
import BasketPayment from '@/components/Basket/basket-payment/BasketPayment.vue'
import BasketDiscount from '@/components/Basket/basket-discount/BasketDiscount.vue'
import DialogBasketProposition from '@/components/Basket/dialogs/DialogBasketProposition.vue'
import BasketOrder from '@/components/Basket/basket-order/BasketOrder.vue'
import BasketClientPack from '@/components/Basket/basket-client-pack/BasketClientPack.vue'
import BasketPricePanel from '@/components/Basket/basket-price-panel/BasketPricePanel.vue'
import BasketGifts from '@/components/Basket/basket-gifts/BasketGifts.vue'

const LoginForm = defineAsyncComponent(
  () => import('@/components/login/LoginForm.vue'),
)

const DialogBasketTransactionTime = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketTransactionTime.vue'),
)
const DialogBasketAddivitesAndPacks = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketAddivitesAndPacks.vue'),
)

// Состояние кнопки "Оформить"
const checkoutButtonState = ref<CheckoutButtonStateType>(null)
const updateCheckoutButtonState = (value: CheckoutButtonStateType) => {
  checkoutButtonState.value = value
}
provide('checkoutButtonState', {
  get: checkoutButtonState,
  set: updateCheckoutButtonState,
})

const { isMobile } = useDevice()
const { $metrics, $sentryCaptureException, $apiService } = useNuxtApp()

const router = useRouter()

const basketStore = useBasketStore()
const userStore = useUserStore()
const { showSnackbar } = useAppStore()

const dialogs = ref({
  userAuthorization: {
    visible: false,
  },
  showProposition: {
    visible: false,
  },
  additivesAndPacks: {
    visible: false,
  },
  transactionTime: {
    visible: false,
  },
})

const EVENT_TYPE_PS = 'payment_started_payselection'

const isOrderLoading = ref(false)
const basketObserver = ref(null)
const isSectionSticky = ref(true)
const propositionShowedAlready = ref(false)

const clientPacksCount = computed(() => basketStore.clientPacksCount)
const clientAdditivesCount = computed(() => basketStore.clientAdditivesCount)

const isClientPackAvailable = computed(() =>
  !basketStore.data.persons ? true : basketStore.data.persons.maxNumber === 0,
)

const basket = computed(() => basketStore.data)

const basketError = computed(() => basketStore.error)

const isAuthenticated = computed(() => userStore.isAuthenticated)

const basketGiftsWithoutChoice = computed(() => basketStore.giftsNoChoice)
const basketGiftsWithChoice = computed(() => basketStore.giftsOfChoice)

const addedAdditives = computed(() => basketStore.addedAdditives)

const selectedPaymentMethod = computed(
  () => basketStore.data.selectedPaymentMethod,
)

const checkSelectedSavedCard = computed(
  () => basketStore.checkSelectedSavedCard,
)

// Следим за подарками - если подарков после обновления стало меньше -> показываем снекбар
watch(
  () => basket.value.gifts,
  (newState, prevState) => {
    const newNoChoiseGifts = newState.filter((gift) => !gift.should_choose)
    const prevNoChoiseGifts = prevState.filter((gift) => !gift.should_choose)
    // Если убрали подарки без выбора
    if (newNoChoiseGifts.length < prevNoChoiseGifts.length)
      showSnackbar({
        message: 'Убрали подарок, проверьте условия для применения',
      })

    const newChoiseGifts = newState.filter((gift) => gift.should_choose)
    const prevChoiseGifts = prevState.filter((gift) => gift.should_choose)
    // Если убрали подарки с выбором
    // Кейс, когда в массиве gifts > 1 массивов с выбором, но мы отображаем всегда только первый
    if (!newChoiseGifts.length && prevChoiseGifts.length)
      showSnackbar({
        message: 'Убрали подарок, проверьте условия для применения',
      })
  },
)

const observeStickySection = () => {
  useIntersectionObserver(
    isAuthenticated.value
      ? '#basket-discount-observable-target'
      : '#basket-note-observable-target',
    {
      threshold: [0, 0.5, 1],
      rootMargin: isMobile ? '0px 0px -150px' : '-150px 0px 0px',
    },
    ([{ isIntersecting, boundingClientRect }]) => {
      isSectionSticky.value = !isIntersecting && boundingClientRect.top > 500
    },
  )
}

const isShowedAdditivesModal = () => {
  if (!clientPacksCount.value || !clientAdditivesCount.value) {
    const showedWarningAdditivesOrClientPack = JSON.parse(
      localStorage.getItem('showedWarningAdditivesOrClientPack') as string,
    )

    // Проверяем было ли отображение предупреждения
    if (!showedWarningAdditivesOrClientPack) {
      dialogs.value.additivesAndPacks.visible = true

      return false
    }
  }

  return true
}

const preClearanceCheckSave = async () => {
  if (!isShowedAdditivesModal()) {
    return
  }

  // Переходим к оформлению заказа
  checkSelectedSavedCard.value
    ? await orderCheckoutForPay()
    : await orderCheckout()
}

// Метод оплаты по сохраненной карте
const orderCheckoutForPay = async () => {
  if (!isAuthenticated.value) {
    dialogs.value.userAuthorization.visible = true

    return
  }

  if (
    basketStore.data.proposition.items.length !== 0 &&
    !propositionShowedAlready.value
  ) {
    dialogs.value.showProposition.visible = true
    propositionShowedAlready.value = true

    return
  }

  dialogs.value.transactionTime.visible = true

  isOrderLoading.value = true
  const orderId = await basketStore.orderCheckout()

  if (basketStore.error) {
    dialogs.value.transactionTime.visible = false
    isOrderLoading.value = false

    return
  }

  const paymentMethodId = (selectedPaymentMethod.value as SavedCard)?.id

  if (paymentMethodId) {
    await $apiService.order.paySavedCardPaySelection(
      String(orderId),
      String(paymentMethodId),
    )

    // Независимо от ответа статуса оплаты, редиректим на страницу заказа
    redirectToOrderPage(orderId)
  }
}

const redirectToOrderPage = (orderId: string) => {
  localStorage.setItem(
    'showedWarningAdditivesOrClientPack',
    JSON.stringify(false),
  )

  router.push({
    path: `/profile/orders/${orderId}/`,
  })
}

const orderCheckout = async () => {
  if (!isAuthenticated.value) {
    dialogs.value.userAuthorization.visible = true

    return
  }

  if (
    basket.value.proposition.items.length !== 0 &&
    !propositionShowedAlready.value
  ) {
    dialogs.value.showProposition.visible = true
    propositionShowedAlready.value = true

    return
  }

  isOrderLoading.value = true
  const orderId = await basketStore.orderCheckout()

  if (basketStore.error) {
    isOrderLoading.value = false
    return
  }

  if (selectedPaymentMethod.value?.type === PaymentMethodType.SBP)
    $metrics?.eventSendGoal({ ym: 'payment_started_SBP' })

  if (selectedPaymentMethod.value?.type === PaymentMethodType.PAY_SELECTION)
    $metrics?.eventSendGoal({ ym: EVENT_TYPE_PS, mt: EVENT_TYPE_PS })

  redirectToOrderPage(orderId)
}

const goToError = (id?: string) => {
  if (id) {
    scrollToBlock(`#${id}`)

    return
  }

  // @ts-ignore
  const errors = Object.keys(basketObserver.value?.getErrors())

  if (!errors.length) return

  id = errors[0].split('-')[0]

  scrollToBlock(`#${id}`)
}

const showError = () => setTimeout(() => goToError(), 0)

// TODO Нужен рефакторинг взаимодействия basketAddresses и basketTime
const triggeredAviableTime = ref(false)
const updateTimeChoices = () =>
  (triggeredAviableTime.value = !triggeredAviableTime.value)

const closePropositionDialog = async (isError: boolean) => {
  dialogs.value.showProposition.visible = false

  if (!isError) {
    checkSelectedSavedCard.value
      ? await orderCheckoutForPay()
      : await orderCheckout()
  } else {
    propositionShowedAlready.value = false

    await basketStore.fetchBasket()
  }
}

watch(basketError, async (basketError) => {
  if (!basketError) return

  if (
    basketError instanceof UnprocessableEntityError &&
    // @ts-ignore
    basketError.data?.product_id
  ) {
    showSnackbar({
      // @ts-ignore
      message: basketError.data?.product_id[0],
      type: SnackbarType.MESSAGE,
    })

    await basketStore.fetchBasket()
  } else if (
    basketError instanceof UnprocessableEntityError &&
    // @ts-ignore
    basketError.data
  ) {
    if (!basketObserver.value) {
      return
    }

    setTimeout(() => {
      // @ts-ignore
      const id = Object.keys(basketError.data)?.[0]

      goToError(id)
    }, 50)
  } else if (basketError instanceof UnprocessableEntityError) {
    showSnackbar({
      message: 'Что-то пошло не так :( Попробуйте позже',
      type: SnackbarType.MESSAGE,
    })

    $sentryCaptureException(basketError)
  } else if (basketError instanceof ServerError) {
    $sentryCaptureException(basketError)
  }
})

const isDialogOnClose = () => (dialogs.value.userAuthorization.visible = false)

onMounted(() => {
  observeStickySection()

  window.carrotquest?.track('cart_viewed', {
    productsId: basket?.value?.items?.map((item: Item) => item.product_id),
    name: basket?.value?.items?.map((item: Item) => item.title),
    amount: basket?.value?.items?.map((item: Item) => item.price),
    img: basket?.value?.items?.map((item: Item) => item.image_url),
  })

  $metrics?.eventSendGoal({ mt: 'startofordering' })
})

const isGiftNeed = ref(basketStore.data.selectedGifts.length !== 0)

const updateIsGiftNeed = (value: boolean) => {
  isGiftNeed.value = value
}

watch(isGiftNeed, () => {
  if (isGiftNeed.value) return

  basketStore.clearSelectedGifts()
})

const goToAuthorize = () => {
  // Сначала показывае МО доборов, потом авторизация
  if (!isShowedAdditivesModal()) {
    return
  }

  dialogs.value.userAuthorization.visible = true
}
</script>

<template>
  <vee-form
    v-slot="{ handleSubmit, errors }"
    ref="basketObserver"
    class="basket__section"
    validate-on-mount
  >
    <basket-order
      class="section order"
      :items="basket.items"
      :gifts="basketGiftsWithoutChoice"
      :additives="addedAdditives"
    />

    <basket-client-pack
      v-if="isClientPackAvailable"
      :client-packs="basket.clientPacks"
      :client-packs-count="clientPacksCount"
      class="section"
    />

    <basket-additive
      v-if="basket.additives.length > 0"
      id="additives"
      :additives="basket.additives"
      class="section additives"
      @order-checkout="orderCheckout"
      @order-checkout-for-pay="orderCheckoutForPay"
    />

    <basket-gifts
      v-if="basketGiftsWithChoice.length !== 0"
      class="section"
      :gifts="basketGiftsWithChoice"
      :is-gift-need="isGiftNeed"
      @update:is-gift-need="updateIsGiftNeed"
    />

    <!-- TODO: Полностью переделать компонент -->
    <basket-addresses
      v-if="isAuthenticated"
      class="section"
      @delivery-method-changed="updateTimeChoices"
    />

    <basket-time
      v-if="isAuthenticated && basketStore.basketAddressSelected"
      :triggered-aviable-time="triggeredAviableTime"
      class="section"
    />

    <basket-payment v-if="isAuthenticated" class="section" />

    <basket-note class="section" />

    <basket-discount class="section" />

    <basket-price-panel
      class="section price"
      :class="{ 'section--sticky': isSectionSticky }"
      :is-section-sticky="isSectionSticky"
      :basket-errors="errors"
      :is-gift-need="isGiftNeed"
      :is-loading="isOrderLoading"
      @go-to-authorize="goToAuthorize"
      @order-checkout="handleSubmit(preClearanceCheckSave), showError()"
    />
  </vee-form>

  <the-dialog v-model="dialogs.userAuthorization.visible" :max-width="560">
    <login-form
      @code-confirmed-by-named-user="isDialogOnClose"
      @set-name="isDialogOnClose"
      @sign-in-by-named-user="isDialogOnClose"
    />
  </the-dialog>

  <dialog-basket-addivites-and-packs
    v-model="dialogs.additivesAndPacks.visible"
    :client-packs="basket.clientPacks"
    :client-packs-count="clientPacksCount"
    :additives="basket.additives"
    :additives-count="clientAdditivesCount"
    @order-checkout="orderCheckout"
    @order-checkout-for-pay="orderCheckoutForPay"
  />

  <dialog-basket-proposition
    v-if="dialogs.showProposition.visible"
    :propositions="basket.proposition.items"
    :time-to-live="basket.proposition.timeToLive"
    @close="closePropositionDialog"
  />

  <the-dialog
    :max-width="360"
    :model-value="dialogs.transactionTime.visible"
    padding="0"
    persistent
  >
    <dialog-basket-transaction-time
      v-click-outside="false"
      class="transaction-dialog"
    />
  </the-dialog>
</template>

<style lang="scss" scoped>
:deep(.v-dialog) {
  overflow: hidden !important;
}

.basket__section {
  position: relative;
  display: grid;
  gap: 60px 0;
  max-width: 620px;
  margin: auto;

  @include media('<tablet') {
    gap: 16px;
  }

  @include media('>=tablet', '<=desktop-mini') {
    margin-bottom: 60px;
  }
  @include media('>=desktop-mini') {
    gap: 20px 0;
    margin-bottom: 30px;
  }

  @include media('height<=desktop-mini-height', '>=desktop-mini') {
    margin-bottom: 0;
  }

  .section {
    position: relative;
    max-width: 620px;
    background-color: $white;
    border-radius: 20px;

    &--sticky {
      filter: drop-shadow(0px -6px 30px rgba(50, 52, 72, 0.3));
      border-radius: 20px 20px 0 0;
      transition: 0.5s ease-in-out;

      @include media('height<=desktop-mini-height', '>=desktop-mini') {
        bottom: -10px !important;
        border-radius: 20px;
      }
    }

    @include media('<desktop-mini') {
      padding: 16px 20px;

      &:first-child {
        border-radius: 0 0 20px 20px;
      }

      &:last-child {
        border-radius: 20px 20px 0 0;
      }
    }

    @include media('>=desktop-mini') {
      padding: 30px;
    }
  }

  .price {
    position: sticky;
    bottom: -1px;
    z-index: 100;
    padding: 25px 30px;
    overflow: hidden;
    transition: 0.3s ease-in-out;

    @include media('<tablet') {
      padding: 16px 20px;
    }
  }
}

:deep(.basket-observable-target) {
  position: absolute;
  right: 0;
  bottom: 100px;
  left: 0;
  z-index: 100;
  height: 10px;
  pointer-events: none;
  visibility: hidden;
  opacity: 0;

  @include media('>=desktop-mini') {
    bottom: -120px;
  }

  @include media('height<=desktop-mini-height', '>=desktop-mini') {
    bottom: -250px;
  }
}
</style>
